import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnFilterRow, applyColumnFilters } from "../../../Components/Common/DashboardKit";
import useTableSort from "../../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Row, Col, Button, Badge, Spinner,
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    DashboardPageHeader,
    DataTableShell,
} from '../../../Components/Common/DashboardKit';
import api from '../../../api';

/**
 * My Training (any authenticated user)
 *
 * Surfaces DmsRevLog rows where:
 *   - the current user is on the document's notification list
 *   - TrainingRequired in ('Y', 'O')
 *   - TrainingCompleted = false
 *   - the parent document Status = 2 (Released)
 *
 * Backend (Phase 2.3):
 *   GET  /api/dms/training-pending?empId=N
 *   POST /api/dms/{dmsId}/revisions/{revLogId}/complete-training  body { completedBy }
 *
 * EmpID is read from sessionStorage.authUser (set by login thunk).
 *
 * Pass 3 UI update (2026):
 *  - Replaced the hand-rolled CardHeader + loading/error/empty/table
 *    blocks with the shared DataTableShell. Same skeleton behavior,
 *    same wording for the empty state ("You're all caught up — no
 *    training pending.") preserved when there's no search filter.
 *  - Removed dead imports (Card, CardBody, CardHeader, Input,
 *    InputGroup, InputGroupText, Table) — DataTableShell now owns
 *    those.
 *  - loadAll, handleComplete, every API endpoint, every payload, and
 *    the optimistic row removal on success are unchanged.
 *  - The empId derivation from sessionStorage.authUser is unchanged.
 *  - docTypeLabel and trainingBadge helpers preserved exactly.
 */

const docTypeLabel = (t) =>
    ({ 1: 'SOP', 2: 'WI', 3: 'FCD', 4: 'ECN', 5: 'LIT' })[t] || `Type ${t}`;

const trainingBadge = (code) =>
    code === 'Y'
        ? <Badge color="danger">Required</Badge>
        : <Badge color="warning">Optional</Badge>;

const MyTraining = () => {
    // empId from session
    const authUser = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem('authUser') || '{}'); }
        catch { return {}; }
    }, []);
    const empId = Number(authUser.uid);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');
    // Round 34 — EPO-style per-column filters + click-to-sort.
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = React.useMemo(() => [
        { id: "docId",    label: "Doc ID",   type: "text", get: (r) => r.docId || "" },
        { id: "type",     label: "Type",     type: "text", get: (r) => docTypeLabel(r.docType) },
        { id: "docTitle", label: "Title",    type: "text", get: (r) => r.docTitle || "" },
        { id: "version",  label: "Rev",      type: "text", get: (r) => String(r.version ?? "") },
        { id: "releasedOn", label: "Released", type: "date", get: (r) => r.releasedOn || "" },
        { id: "training", label: "Training", type: "text", get: (r) => (r.trainingRequired === "Y" ? "Required" : "Optional") },
        { id: "act",      label: "Action",   type: "none" },
    ], []);
    const [savingId, setSavingId] = useState(null);

    const loadAll = useCallback(async () => {
        if (!empId) { setLoading(false); setLoadError('No user in session.'); return; }
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get('dms/training-pending', { params: { empId } });
            setRows(res.data || []);
        } catch (err) {
            setLoadError(err?.response?.data?.error || err?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    }, [empId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.docId, r.docTitle, docTypeLabel(r.docType)]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    const handleComplete = async (r) => {
        setSavingId(r.revLogId);
        try {
            // Go-live parity (2026-09-24): the API route carries the document id
            // (POST dms/{dmsId}/revisions/{revLogId}/complete-training) — the old
            // dms/revisions/{revLogId}/… URL does not exist and always 404'd.
            await api.post(`dms/${r.dmsId}/revisions/${r.revLogId}/complete-training`, {
                completedBy: empId,
            });
            setRows((prev) => prev.filter((x) => x.revLogId !== r.revLogId));
            toast.success(`Marked training complete for ${r.docId}.`);
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to mark complete.');
        } finally {
            setSavingId(null);
        }
    };

    // Wrap the actual <table> body. DataTableShell renders the chrome
    // around it (search, refresh, count, skeleton, empty, error).
    const tableBody = (
        <div className="table-responsive">
            <table className="table align-middle table-nowrap table-hover mb-0">
                <thead className="table-light">
                    <tr>
                        {tblColumns.slice(0, 6).map((c) => (
                            <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                        ))}
                        <th className="text-end">Action</th>
                    </tr>
                    <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                </thead>
                <tbody>
                    {view.map((r) => (
                        <tr key={r.revLogId}>
                            <td><code>{r.docId}</code></td>
                            <td><Badge color="info" pill>{docTypeLabel(r.docType)}</Badge></td>
                            <td>{r.docTitle || <span className="text-muted">—</span>}</td>
                            <td>{r.version ?? '—'}</td>
                            <td>{r.releasedOn ? new Date(r.releasedOn).toLocaleDateString() : <span className="text-muted">—</span>}</td>
                            <td>{trainingBadge(r.trainingRequired)}</td>
                            <td className="text-end">
                                <Button
                                    color="success"
                                    size="sm"
                                    disabled={savingId === r.revLogId}
                                    onClick={() => handleComplete(r)}
                                >
                                    {savingId === r.revLogId ? (
                                        <><Spinner size="sm" /> Marking…</>
                                    ) : (
                                        <><i className="ri-check-line align-bottom me-1"></i>Mark Complete</>
                                    )}
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-graduation-cap-line"
                    title="My Training"
                    subtitle="Document revisions requiring your training acknowledgement"
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "DMS", to: "/workflows?wf=dms" },
                        { label: "My Training" },
                    ]}
                />

                <Row>
                    <Col xs={12}>
                        <DataTableShell
                            title="Pending training acknowledgements"
                            subtitle="Released documents flagged Required (Y) or Optional (O) where you're a notification recipient."
                            rowCount={filtered.length}
                            countLabel="documents"
                            searchPlaceholder="Search doc ID or title…"
                            onRefresh={loadAll}
                            actions={
                                /* Round 29 — CSV of the filtered list via the shell's actions slot */
                                <button
                                    type="button"
                                    className="btn btn-soft-primary btn-sm"
                                    onClick={() =>
                                        exportRowsToCsv(
                                            "dms-my-training.csv",
                                            ((r0) => Object.keys(r0 || {}).map((k) => ({ key: k, label: k })))(filtered[0]),
                                            filtered
                                        )
                                    }
                                >
                                    <i className="ri-download-2-line align-bottom me-1" />Export CSV ({filtered.length})
                                </button>
                            }
                            refreshing={loading}
                            loading={loading && rows.length === 0}
                            error={loadError}
                            empty={!loading && !loadError && filtered.length === 0}
                            emptyIcon={search ? 'ri-search-line' : 'ri-emotion-happy-line'}
                            emptyTitle={search ? 'No matches' : 'You\'re all caught up'}
                            emptyMessage={
                                search
                                    ? 'No documents match your search.'
                                    : 'No training is pending right now.'
                            }
                            skeletonColumns={7}
                        >
                            {tableBody}
                        </DataTableShell>
                    </Col>
                </Row>
            </Container>
            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
};

export default MyTraining;
